import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../services/authService';
import { getTenantFromCookie, redirectToTenant } from '../services/clientService';
import styles from './AuthWrapper.module.css';

interface AuthWrapperProps {
  children: React.ReactNode;
}

// Routes that do NOT require the user to be authenticated
const PUBLIC_ROUTES = [
  '/login',
  '/register',
  '/forgot-password',
  '/google-callback',
  '/complete-profile',
  '/verify-email',
  '/confirm-forgot-password',
];

function AuthWrapper({ children }: AuthWrapperProps) {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const tenant = getTenantFromCookie();

  useEffect(() => {
    if (!tenant) return;

    const checkAuth = () => {
      const authStatus = isAuthenticated();
      setAuthenticated(authStatus);

      const isPublic = PUBLIC_ROUTES.includes(location.pathname);

      if (!authStatus && !isPublic) {
        // Protected route and not authenticated → login
        navigate('/login');
      } else if (authStatus && location.pathname === '/login') {
        // Already authenticated and visiting the login page → redirect to tenant domain
        redirectToTenant(tenant);
      }
    };

    checkAuth();
  }, [location.pathname, navigate, tenant]);

  // Block all usage if the CLINARYA_SUBDOMAIN cookie is absent.
  // The cookie is set by the external system when redirecting to this portal.
  if (!tenant) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loginBox}>
          <h2 className={styles.loginTitle}>Acceso no permitido</h2>
          <p className={styles.loginSubtitle}>
            No se encontró el contexto de organización necesario para acceder a este
            portal. Por favor, acceda desde el enlace proporcionado por su organización.
          </p>
        </div>
      </div>
    );
  }

  if (authenticated === null) {
    return (
      <div className={styles.loadingContainer}>
        <div>Cargando...</div>
      </div>
    );
  }

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  if (isPublicRoute || authenticated) {
    return <>{children}</>;
  }

  // Not authenticated on a protected route – will redirect via effect
  return null;
}

export default AuthWrapper;
